import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Stack, IconButton, Typography, Divider, Grid, Chip, Card, TextField, CircularProgress, MenuItem, Button, Select, FormControl, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Snackbar, Alert } from '@mui/material';
import { ArrowBack, Edit, Delete, Groups, Person, CalendarMonth, Timer, CheckCircleOutline, Description, Add, AccountTree, HistoryEdu, AttachFile, Visibility, AssignmentTurnedIn, PlaylistAddCheck, Bookmark, Flag, DonutLarge, Save, RateReview, Close, ChatBubbleOutline } from '@mui/icons-material';
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData, putData, deleteData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';
import CreateSubTask from '../subTaskManagement/createSubTask';
import Loader from '../../../../common/spinner';
import { getStatusColor, priorityColors, getAvatarColor } from '../../../../utils/statusColor';

const UserTaskDetails = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const taskId = searchParams.get("taskId");
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';

    const [state, setState] = useState({ loading: true, taskData: null, error: null, isEditable: false, isUpdating: false, isDeleting: false });
    const [dialogs, setDialogs] = useState({ delete: false, subTask: false, subTaskDelete: null, isTaskDeleting: false });
    const [formData, setFormData] = useState({});
    const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 5 });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [errors, setErrors] = useState({});
    const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));
    const updateDialogs = (updates) => setDialogs(prev => ({ ...prev, ...updates }));

    const remainingHours = useMemo(() => {
        if (!state.taskData) return 0;

        const mainTotal = parseFloat(state.taskData.estimated_hours) || 0;
        const subtasksTotal = state.taskData.subtasks?.reduce((sum, sub) => {
            return sum + (parseFloat(sub.estimated_hours) || 0);
        }, 0) || 0;

        return mainTotal - subtasksTotal;
    }, [state.taskData]);

    const fetchTaskDetails = useCallback(async () => {
        try {
            updateState({ loading: true });
            const res = await getData(API_ENDPOINTS.fetchTaskById(taskId));

            if (res.status === "SUCCESS") {
                const task = res.result;

                if (task.assignees && Array.isArray(task.assignees)) {
                    task.assignees = task.assignees.filter((value, index, self) =>
                        index === self.findIndex((t) => t.id === value.id)
                    );
                }

                updateState({ taskData: task, error: null });
                setFormData({ ...task, comment: '', employee_id: userId, task_id: task.id });
            } else {
                updateState({ error: res.message || "Failed to fetch task details" });
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message;
            updateState({ error: errorMsg });
        } finally {
            updateState({ loading: false });
        }
    }, [taskId, userId]);

    useEffect(() => { if (taskId) fetchTaskDetails(); }, [taskId, fetchTaskDetails]);

    const handleBackNavigation = () => {
        navigate(-1);
    };

    const handleAction = async (type, payload = null) => {
        try {
            if (type === 'update') {
                const needsReason = ['Hold', 'Re Open'].includes(formData.status);
                if (needsReason && (!formData.reason || formData.reason.trim() === "")) {
                    setErrors(prev => ({ ...prev, reason: true }));
                    setSnackbar({ open: true, message: 'Please provide a reason for the status change', severity: 'error' });
                    return;
                }

                updateState({ isUpdating: true });
                const updatedFields = {};
                const keysToCompare = [
                    'task_title', 'task_type', 'priority', 'status',
                    'progress', 'description', 'comment', 'actual_hours', 'reason' // Added reason
                ];

                keysToCompare.forEach(key => {
                    if (formData[key] !== state.taskData[key]) {
                        updatedFields[key] = formData[key];
                    }
                });

                const finalPayload = {
                    ...updatedFields,
                    id: state.taskData.id,
                    employee_id: userId,
                    user_id: parseInt(userId),
                    user_type: userRole,
                    comments: formData.comment && formData.comment.trim() ? [formData.comment] : [],
                };

                if (formData.comment && formData.comment.trim() !== "") {
                    finalPayload.comment = formData.comment;
                }

                if (needsReason) {
                    finalPayload.reason = formData.reason;
                }

                const hasChanges = Object.keys(updatedFields).length > 0 || formData.comment;

                if (hasChanges) {
                    await putData(API_ENDPOINTS.updateUserTaskById, finalPayload);
                    setErrors({});
                    setSnackbar({ open: true, message: 'Task updated successfully', severity: 'success' });
                }

                updateState({ isEditable: false });
                fetchTaskDetails();
            }

            // --- Added Main Task Delete Logic ---
            else if (type === 'deleteMain') {
                updateState({ isDeleting: true });
                const res = await deleteData(`${API_ENDPOINTS.deleteUserTaskById}/${state.taskData.id}`);

                if (res.status === "SUCCESS") {
                    setSnackbar({ open: true, message: 'Task deleted successfully', severity: 'success' });
                    updateDialogs({ delete: false });
                    // Navigate back to the list/parent since this task no longer exists
                    setTimeout(() => navigate(-1), 1000);
                }
            }

            // --- Added Sub-Task Delete Logic ---
            else if (type === 'deleteSub') {
                const subTaskId = dialogs.subTaskDelete?.id;
                if (!subTaskId) return;

                updateDialogs({ isTaskDeleting: true });
                const res = await deleteData(`${API_ENDPOINTS.deleteUserTaskById}/${subTaskId}`);

                if (res.status === "SUCCESS") {
                    setSnackbar({ open: true, message: 'Sub-task deleted successfully', severity: 'success' });
                    updateDialogs({ subTaskDelete: null });
                    fetchTaskDetails(); // Refresh the list to show remaining sub-tasks
                }
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Operation failed";
            setSnackbar({ open: true, message: `Error: ${errorMsg}`, severity: 'error' });
        } finally {
            updateState({ isUpdating: false, isDeleting: false });
            updateDialogs({ isTaskDeleting: false });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updatedValue = value;

        if (name === 'comment' && value.length > 0) {
            updatedValue = value.charAt(0).toUpperCase() + value.slice(1);
        }

        if (name === 'status') {
            if (value === 'On Hold') updatedValue = 'Hold';
        }

        let updatedData = { ...formData, [name]: updatedValue };

        if (name === 'status') {
            if (updatedValue === 'Completed') {
                updatedData.progress = 100;
            } else {
                // If any other status is selected, reset progress to 0
                updatedData.progress = 0;
            }

            // Clear reason if switching away from Hold/Reopen
            if (!['Hold', 'Re Open'].includes(updatedValue)) {
                updatedData.reason = "";
                setErrors(prev => ({ ...prev, reason: false }));
            }
        }

        if (name === 'reason' && value.trim() !== "") {
            setErrors(prev => ({ ...prev, reason: false }));
        }
        setFormData(updatedData);
    };

    const handleAddSubTaskClick = () => {
        if (state.taskData.task_type === "Single") {
            setSnackbar({
                open: true,
                message: 'Subtasks can’t be added to a single task.',
                severity: 'warning'
            });
        } else {
            updateDialogs({ subTask: true });
        }
    };

    const metaItems = useMemo(() => {
        if (!state.taskData) return [];
        const t = state.taskData;

        return [
            { label: 'Project', val: t.Project?.project_title, icon: <AccountTree fontSize="small" /> },
            { label: 'Epic', val: t.Epic?.title || 'No Epic Linked', icon: <AssignmentTurnedIn fontSize="small" /> },
            { label: 'Story', val: t.UserStory?.title || 'No Story Linked', icon: <Bookmark fontSize="small" /> },
            { label: 'Task Type', val: t.task_type || 'Unassigned', icon: <Person fontSize="small" /> },
            { label: 'Work Type', val: t.work_type || 'Unassigned', icon: <Groups fontSize="small" /> },
            {
                label: 'Timeline',
                val: `${new Date(t.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
                icon: <CalendarMonth fontSize="small" />
            },
            { label: 'Estimated Time', val: `${t.estimated_hours || 0} hrs`, icon: <Timer fontSize="small" /> },
            { label: 'Actual Time', val: `${t.actual_hours || 0} hrs`, icon: <CheckCircleOutline fontSize="small" />, color: '#10b981' },
            {
                label: 'Priority',
                val: t.priority,
                icon: <Flag fontSize="small" />,
                isChip: true,
                isEditable: true,
                name: 'priority',
                options: Object.keys(priorityColors),
                colors: priorityColors[t.priority]
            },
            {
                label: 'Status',
                val: t.status === 'Hold' ? 'On Hold' : t.status,
                icon: <DonutLarge fontSize="small" />,
                isChip: true,
                isEditable: true,
                name: 'status',
                options: ['Todo', 'In Progress', 'On Hold', 'Re Open', 'Completed'],
                // Use the function from utility
                colors: getStatusColor(t.status)
            },
        ];
    }, [state.taskData]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    if (state.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Loader /></Box>;
    if (state.error || !state.taskData) {
        return (
            <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>{state.error || "Task not found"}</Typography>
                <Button variant="outlined" onClick={handleBackNavigation} startIcon={<ArrowBack />} sx={{ textTransform: 'none', borderRadius: '8px' }}>Back to Story</Button>
            </Box>
        );
    }

    return (
        <>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', px: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: '#64748b', borderRadius: '0px', p: 1, '&:hover': { bgcolor: '#e0e3e6' } }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: 1 }}>
                    <Typography variant="h6" fontWeight="bold">User Task Details</Typography>
                    {state.isEditable && (
                        <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                            <Button variant="contained" size="small" startIcon={state.isUpdating ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={() => handleAction('update')} disabled={state.isUpdating} sx={{ bgcolor: '#10b981', textTransform: 'none' }}>Save Changes</Button>
                            <Button variant="outlined" size="small" startIcon={<Close />} onClick={() => updateState({ isEditable: false })} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                        </Stack>
                    )}
                </Box>
            </Stack>

            <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8.5 }}>
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}><span style={{ color: '#3b82f6' }}>#{state.taskData.task_code}</span> {state.taskData.task_title}</Typography>
                                {!state.isEditable && <Stack direction="row" spacing={1}><IconButton onClick={() => updateState({ isEditable: true })}><Edit sx={{ color: '#f59e0b' }} /></IconButton><IconButton onClick={() => updateDialogs({ delete: true })}><Delete sx={{ color: '#ef4444' }} /></IconButton></Stack>}
                            </Box>

                            <Grid container spacing={2}>
                                {/* Meta Grid Mapping Section */}
                                {metaItems.map((item, idx) => (
                                    <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            {/* 1. Standardize Icon Column Color */}
                                            <Box sx={{ color: item.isChip ? item.colors?.color : '#94a3b8', display: 'flex' }}>
                                                {item.icon}
                                            </Box>

                                            <Typography variant="body2" sx={{ color: '#64748b', minWidth: 100 }}>
                                                {item.label}
                                            </Typography>

                                            {state.isEditable && item.isEditable ? (
                                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                                    <Select
                                                        name={item.name}
                                                        value={item.name === 'status' && formData[item.name] === 'Hold' ? 'On Hold' : formData[item.name]}
                                                        onChange={handleInputChange}
                                                        sx={{ height: 32 }}
                                                    >
                                                        {item.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            ) : item.isChip ? (
                                                // 2. Fix Chip styles to use .bg, .color, and .border
                                                <Chip
                                                    label={item.val}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: item.colors?.bg,
                                                        color: item.colors?.color,
                                                        fontWeight: 600,
                                                        px: 2,
                                                        border: `1px solid ${item.colors?.border}` // Explicitly apply the utility border
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {item.val}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>

                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', display: 'flex', gap: 1 }}><Description sx={{ color: '#0369a1' }} /><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0369a1' }}>Description</Typography></Box>
                                <Box sx={{ p: 2 }}><Typography variant="body2">{state.taskData.description}</Typography></Box>
                            </Card>

                            {/* Attachments Section */}
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f5f3ff', display: 'flex', gap: 1 }}><AttachFile sx={{ color: '#5b21b6', transform: 'rotate(45deg)' }} /><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#5b21b6' }}>Attachments ({state.taskData.attachments?.length || 0})</Typography></Box>
                                <Box sx={{ p: 2 }}>
                                    {state.taskData.attachments?.length > 0 ? state.taskData.attachments.map((file, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #f1f5f9', mb: 1 }}>
                                            <Stack direction="row" spacing={1}><Description sx={{ color: '#94a3b8' }} /><Typography variant="body2" sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.file_name}</Typography></Stack>
                                            <IconButton size="small" onClick={() => window.open(file.file_url, '_blank')}><Visibility sx={{ color: '#64748b', fontSize: 20 }} /></IconButton>
                                        </Box>
                                    )) : <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No files uploaded.</Typography>}
                                </Box>
                            </Card>

                            {/* Sub-Tasks Section */}
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', display: 'flex', justifyContent: 'space-between' }}>
                                    <Stack direction="row" spacing={1}>
                                        <PlaylistAddCheck sx={{ color: '#0369a1' }} />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0369a1' }}>Sub-Tasks ({state.taskData.subtasks?.length || 0})</Typography>
                                            <Typography variant="caption">({state.taskData.subtasks?.filter(st => st.status === "Completed").length || 0} completed of {state.taskData.subtasks?.length || 0})</Typography>
                                        </Box>
                                    </Stack>
                                    <IconButton size="small" onClick={handleAddSubTaskClick}><Add /></IconButton>
                                </Box>
                                {state.taskData.subtasks?.length > 0 ? (
                                    <>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                                    <TableRow>
                                                        <TableCell>Sub-Task Name</TableCell>
                                                        <TableCell>Work Type</TableCell>
                                                        <TableCell>Assignee</TableCell>
                                                        <TableCell>Estimated Hours</TableCell>
                                                        <TableCell>Actual Hours</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {state.taskData.subtasks
                                                        .slice(pagination.page * pagination.rowsPerPage, pagination.page * pagination.rowsPerPage + pagination.rowsPerPage)
                                                        .map((task, i) => (
                                                            <TableRow key={i} hover>
                                                                <TableCell>{task.task_title}</TableCell>
                                                                <TableCell>{task.work_type}</TableCell>
                                                                <TableCell>
                                                                    <Stack direction="row" spacing={-1}>
                                                                        {task.assignees?.length > 0 ? (
                                                                            task.assignees.map((user, idx) => (
                                                                                <Tooltip key={idx} title={`${user.first_name} ${user.last_name}`} arrow>
                                                                                    <Box sx={{ width: 28, height: 28, bgcolor: '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#475569', border: '2px solid #fff' }}>
                                                                                        {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()}
                                                                                    </Box>
                                                                                </Tooltip>
                                                                            ))
                                                                        ) : <Person sx={{ fontSize: 16, color: '#cbd5e1' }} />}
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>{task.estimated_hours} hrs</TableCell>
                                                                <TableCell>{task.actual_hours || 0} hrs</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={task.status}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: getStatusColor(task.status).bg,
                                                                            color: getStatusColor(task.status).color,
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${getStatusColor(task.status).border || '#cbd5e1'}`
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <IconButton size="small" onClick={() => navigate(`${roleBasePath}/user-subtask-details?subTaskId=${task?.id}`)}><Visibility sx={{ fontSize: 18 }} /></IconButton>
                                                                    <IconButton size="small" onClick={() => updateDialogs({ subTaskDelete: task })}><Delete sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            component="div"
                                            count={state.taskData.subtasks.length}
                                            page={pagination.page}
                                            onPageChange={(_, p) => setPagination(prev => ({ ...prev, page: p }))}
                                            rowsPerPage={pagination.rowsPerPage}
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                            onRowsPerPageChange={(e) =>
                                                setPagination({
                                                    page: 0,
                                                    rowsPerPage: parseInt(e.target.value, 10)
                                                })
                                            }
                                        />
                                    </>
                                ) : <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="textSecondary">No Sub-Tasks Found</Typography></Box>}
                            </Card>

                            {state.isEditable && (formData.status === 'Hold' || formData.status === 'Re Open') && (
                                <Card variant="outlined" sx={{ borderRadius: 3, border: errors.reason ? '1px solid #ef4444' : '1px solid #e2e8f0', mb: 3 }}>
                                    <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderBottom: '1px solid #fee2e2', display: 'flex', gap: 1 }}>
                                        <RateReview sx={{ color: '#dc2626' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                                            {formData.status === 'Hold' ? 'Hold Reason' : 'Re-Open Reason'} <span style={{ color: '#ef4444' }}>*</span>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            name="reason"
                                            error={!!errors.reason}
                                            helperText={errors.reason ? "Reason is mandatory" : ""}
                                            value={formData.reason || ''}
                                            onChange={handleInputChange}
                                            placeholder="Explain why this task is being updated..."
                                        />
                                    </Box>
                                </Card>
                            )}

                            {/* REASON HISTORY LIST (Fetched from response) */}
                            {state.taskData.reasons && state.taskData.reasons.length > 0 && (
                                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 1 }}>
                                        <HistoryEdu sx={{ color: '#64748b' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', lineHeight: 2 }}>
                                            Status Change History ({state.taskData.reasons.length})
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <Stack spacing={2}>
                                            {state.taskData.reasons.map((r, i) => (
                                                <Box key={i} sx={{ p: 1.5, bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 2 }}>
                                                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                                                            ENTRY #{state.taskData.reasons.length - i}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                            {formatDate(r.createdAt)}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                        {r.reason}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Card>
                            )}

                            {/* Comments Section */}
                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 1 }}><ChatBubbleOutline sx={{ color: '#64748b' }} /><Typography variant="subtitle2">Comments ({state.taskData.comments?.length || 0})</Typography></Box>
                                <Box sx={{ p: 2 }}>
                                    {state.isEditable && <TextField name="comment" fullWidth multiline rows={2} placeholder="Add comment..." value={formData.comment} onChange={handleInputChange} sx={{ mb: 2 }} />}
                                    {state.taskData.comments?.length > 0 ? (
                                        <Stack spacing={2}>
                                            {state.taskData.comments.map((c, i) => (
                                                <Stack key={i} direction="row" spacing={2}>
                                                    <Box sx={{ width: 42, height: 42, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569' }}>
                                                            {/* Ensures both initials are capitalized */}
                                                            {`${c?.first_name?.charAt(0) || ''}${c?.last_name?.charAt(0) || ''}`.toUpperCase()}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                {c?.first_name} {c?.last_name}
                                                                <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.7rem', ml: 1 }}>• {c.user_type}</Typography>
                                                            </Typography>
                                                            <Typography variant="caption">{formatDate(c.createdAt)}</Typography>
                                                        </Box>
                                                        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '0 12px 12px 12px', mt: 0.5 }}>
                                                            <Typography variant="body2">{c.comment}</Typography>
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    ) : <Typography variant="body2" color="textSecondary" align="center">No comments yet.</Typography>}
                                </Box>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* Sidebar: Progress & Assignees */}
                    <Grid size={{ xs: 12, md: 3.5 }}>
                        <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">Progress</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{state.isEditable ? formData.progress : state.taskData.progress || 0}%</Typography>
                            </Box>
                            {state.isEditable && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    name="progress"
                                    // REQUIREMENT: Use empty string if value is 0 or null so input is clear
                                    value={formData.progress === 0 ? '' : formData.progress ?? ''}
                                    placeholder="0"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '') {
                                            setFormData({ ...formData, progress: '' });
                                            return;
                                        }
                                        // REQUIREMENT: Max 100% only
                                        let num = Math.min(100, Math.max(0, parseInt(val, 10)));
                                        setFormData({ ...formData, progress: num });
                                    }}
                                    onBlur={() => {
                                        // Revert to 0 if left empty on blur
                                        if (formData.progress === '' || formData.progress === null) {
                                            setFormData({ ...formData, progress: 0 });
                                        }
                                    }}
                                    sx={{ mt: 1 }}
                                />
                            )}
                            <Box sx={{ width: '100%', bgcolor: '#e2e8f0', borderRadius: 10, height: 8, mt: 1 }}>
                                <Box sx={{ width: `${state.isEditable ? formData.progress : state.taskData.progress || 0}%`, bgcolor: '#3b82f6', borderRadius: 10, height: '100%', transition: 'width 0.3s ease' }} />
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 2 }}>ASSIGNED TO</Typography>
                            {/* Sidebar - Assigned To Mapping */}
                            <Stack spacing={2}>
                                {[...new Map(state.taskData.assignees?.map(item => [item.id, item])).values()].map((p, i) => {
                                    const avatarStyle = getAvatarColor(p.first_name);
                                    return (
                                        <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    // 2. Use .bg and .border
                                                    bgcolor: avatarStyle.bg,
                                                    color: '#fff',
                                                    border: `1px solid ${avatarStyle.border}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                                    {`${p?.first_name?.charAt(0) || ''}${p?.last_name?.charAt(0) || ''}`}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                                {p.first_name} {p.last_name}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Delete Dialogs & Subtask Dialog */}
            <Dialog open={dialogs.delete} onClose={() => updateDialogs({ delete: false })} PaperProps={{ sx: { borderRadius: '12px' } }} fullWidth maxWidth="sm">
                <DialogTitle>Delete Task?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{state.taskData?.task_title}</strong>?
                        This action cannot be undone and the task will be permanently removed from the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => updateDialogs({ delete: false })} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={() => handleAction('deleteMain')} variant="contained" color="error" disabled={state.isDeleting} sx={{ textTransform: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogs.subTask} onClose={() => updateDialogs({ subTask: false })} fullWidth maxWidth="md">
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                            Create Sub-Task
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => updateDialogs({ subTask: false })}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            ml: 'auto'
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <CreateSubTask taskData={state.taskData} onClose={() => updateDialogs({ subTask: false })} onSuccess={fetchTaskDetails} remainingHours={remainingHours} />
                </DialogContent>
            </Dialog>

            <Dialog open={Boolean(dialogs.subTaskDelete)} onClose={() => updateDialogs({ subTaskDelete: null })} PaperProps={{ sx: { borderRadius: '12px' } }} fullWidth maxWidth="sm">
                <DialogTitle>Delete Sub-Task?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{dialogs.subTaskDelete?.task_title}</strong>?
                        This action cannot be undone and the task will be permanently removed from the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => updateDialogs({ subTaskDelete: null })} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={() => handleAction('deleteSub')} variant="contained" color="error" disabled={dialogs.isTaskDeleting} sx={{ textTransform: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default UserTaskDetails;